import { useState, useEffect, useCallback } from "react";
import {
  ValidationState,
  DataProfile,
} from "datasets/pages/tables/components/file-validation/types";

// Fixed import path

export const useValidationAndProfiling = (fileId: string | null) => {
  const [validationState, setValidationState] = useState<ValidationState>({
    jobs: [
      // Add initial mock jobs for demo
      {
        title: "File Format Detection",
        description: "Confirming that this is a tabular file",
        result: "Confirmed: This is a tabular CSV file",
        status: "passed",
      },
      {
        title: "Header Detection",
        description: "Detecting if the file has a header row",
        result: "Header row detected with 5 columns",
        status: "passed",
        details: "Column names: patient_id, age, gender, diagnosis, treatment_date",
      },
      {
        title: "File Structure",
        description: "Analyzing file structure and detecting parsing errors",
        result: "File structure: 30 total rows, 5 columns",
        status: "passed",
        details: "All rows parsed successfully with correct structure",
      },
    ],
    summary: {
      totalRows: 31,
      headerRow: true,
      dataRows: 30,
      errorRows: 0,
      cleanRows: 30,
    },
    isComplete: true, // Set to true so we see the summary
    hasErrors: false,
  });

  const [dataProfile, setDataProfile] = useState<DataProfile | null>({
    // Add initial mock profile for demo
    columns: [
      { name: "patient_id", type: "string", categorical: false, nullPercent: 0 },
      { name: "age", type: "integer", categorical: false, nullPercent: 2.3 },
      { name: "gender", type: "string", categorical: true, nullPercent: 0.8 },
      { name: "diagnosis", type: "string", categorical: true, nullPercent: 1.2 },
      { name: "treatment_date", type: "date", categorical: false, nullPercent: 0 },
    ],
    statistics: {
      totalColumns: 5,
      categoricalColumns: 2,
      goodRows: 30,
      errorRows: 0,
    },
  });

  const [isValidating, setIsValidating] = useState(false);
  const [isProfiling, setIsProfiling] = useState(false);

  const startValidation = useCallback(async (fileId: string) => {
    if (!fileId) {
      return;
    }

    setIsValidating(true);
    setValidationState((prev) => ({
      ...prev,
      jobs: [
        {
          title: "File Format Detection",
          description: "Confirming that this is a tabular file",
          result: "Starting validation...",
          status: "running",
        },
      ],
      isComplete: false,
    }));

    try {
      // Mock progressive validation
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setValidationState({
        jobs: [
          {
            title: "File Format Detection",
            description: "Confirming that this is a tabular file",
            result: "Confirmed: This is a tabular CSV file",
            status: "passed",
          },
          {
            title: "Header Detection",
            description: "Detecting if the file has a header row",
            result: "Header row detected with 5 columns",
            status: "passed",
            details: "Column names: patient_id, age, gender, diagnosis, treatment_date",
          },
          {
            title: "File Structure",
            description: "Analyzing file structure and detecting parsing errors",
            result: "File structure: 30 total rows, 5 columns",
            status: "passed",
            details: "All rows parsed successfully with correct structure",
          },
        ],
        summary: {
          totalRows: 31,
          headerRow: true,
          dataRows: 30,
          errorRows: 0,
          cleanRows: 30,
        },
        isComplete: true,
        hasErrors: false,
      });
    } catch (error) {
      console.error("Validation failed:", error);
    } finally {
      setIsValidating(false);
    }
  }, []);

  const startProfiling = useCallback(async (fileId: string) => {
    if (!fileId) {
      return;
    }

    setIsProfiling(true);

    try {
      // Mock profiling
      await new Promise((resolve) => setTimeout(resolve, 1500));

      setDataProfile({
        columns: [
          { name: "patient_id", type: "string", categorical: false, nullPercent: 0 },
          { name: "age", type: "integer", categorical: false, nullPercent: 2.3 },
          { name: "gender", type: "string", categorical: true, nullPercent: 0.8 },
          { name: "diagnosis", type: "string", categorical: true, nullPercent: 1.2 },
          { name: "treatment_date", type: "date", categorical: false, nullPercent: 0 },
        ],
        statistics: {
          totalColumns: 5,
          categoricalColumns: 2,
          goodRows: 30,
          errorRows: 0,
        },
      });
    } catch (error) {
      console.error("Profiling failed:", error);
    } finally {
      setIsProfiling(false);
    }
  }, []);

  // Auto-start validation when file is provided
  useEffect(() => {
    if (fileId && !validationState.isComplete) {
      startValidation(fileId);
    }
  }, [fileId, startValidation, validationState.isComplete]);

  // Auto-start profiling when validation completes
  useEffect(() => {
    if (fileId && validationState.isComplete && !dataProfile && !isProfiling) {
      startProfiling(fileId);
    }
  }, [fileId, validationState.isComplete, dataProfile, isProfiling, startProfiling]);

  return {
    validationState,
    dataProfile,
    isValidating,
    isProfiling,
    startValidation,
    startProfiling,
  };
};
